/**
 * Integer arithmetic for paise and milli-units.
 *
 * The ONLY rounding primitive in DNR Vyapar 2.0. Everything that needs to
 * round goes through `roundHalfUp`, which is half-up AWAY FROM ZERO.
 *
 * Why not banker's rounding (HALF_EVEN)? Indian invoicing practice, and
 * virtually all Indian billing software, rounds 0.5 away from zero. Making
 * that explicit beats inheriting `Math.round`'s behaviour by accident.
 */
import type { Money, Quantity } from './types';
import { asMoney, asQuantity } from './types';

/** Half-up, away from zero. -0.5 → -1, 0.5 → 1, 1.5 → 2, -1.5 → -2. */
export function roundHalfUp(n: number): number {
  return n < 0 ? -Math.round(-n) : Math.round(n);
}

/** roundHalfUp, returned as Money. */
export function roundPaise(n: number): Money {
  return asMoney(roundHalfUp(n));
}

export function addMoney(a: Money, b: Money): Money {
  return asMoney(a + b);
}

export function subtractMoney(a: Money, b: Money): Money {
  return asMoney(a - b);
}

export function sumMoney(values: readonly Money[]): Money {
  let total = 0;
  for (const v of values) total += v;
  return asMoney(total);
}

export function addQuantity(a: Quantity, b: Quantity): Quantity {
  return asQuantity(a + b);
}

export function sumQuantity(values: readonly Quantity[]): Quantity {
  let total = 0;
  for (const v of values) total += v;
  return asQuantity(total);
}

/**
 * quantity (milli-units) × rate (paise per whole unit)
 * = paise, scaled by 1/1000 and rounded half-up.
 *
 * 750 milli (0.75) × 10000 paise (₹100.00) = 7500 paise (₹75.00)
 */
export function quantityTimesRate(quantityMilli: Quantity, ratePaise: Money): Money {
  return roundPaise((quantityMilli * ratePaise) / 1000);
}

/** Percentage of a Money value: round(amount × percentMilli / 100_000). */
export function percentOfMoney(amount: Money, percentMilli: number): Money {
  return roundPaise((amount * percentMilli) / 100_000);
}

/** Tax on a Money value from basis points: round(amount × bp / 10_000). */
export function taxOnMoney(amount: Money, rateBp: number): Money {
  return roundPaise((amount * rateBp) / 10_000);
}

/** Clamp to a non-negative Money. Used when a discount exceeds the line. */
export function clampNonNegative(value: Money): Money {
  return value < 0 ? asMoney(0) : value;
}

/**
 * Distribute `totalPaise` across `weights` proportionally, using the
 * LARGEST-REMAINDER (Hamilton) method.
 *
 * This is why a document-level discount is pushed down onto lines rather
 * than applied to the grand total: a GST return reports per-line taxable
 * values, so the discount has to reach the lines. And the parts MUST sum
 * exactly to the whole — otherwise the invoice does not reconcile.
 *
 * Invariant: Σ result === totalPaise, exactly, for every input.
 * 300 × ₹0.33 split three ways yields 33 / 33 / 33, never 32 / 33 / 34.
 */
export function allocate(weights: readonly number[], totalPaise: number): number[] {
  const n = weights.length;
  if (n === 0) return [];
  if (totalPaise <= 0) return new Array<number>(n).fill(0);

  const weightSum = weights.reduce((a, b) => a + b, 0);

  // Degenerate: nothing to weight by. Put it all on the first line so the
  // total is still conserved, and let the caller decide if that is valid.
  if (weightSum <= 0) {
    const out = new Array<number>(n).fill(0);
    out[0] = totalPaise;
    return out;
  }

  const exact = weights.map((w) => (totalPaise * w) / weightSum);
  const floors = exact.map((e) => Math.floor(e));
  let remainder = totalPaise - floors.reduce((a, b) => a + b, 0);

  // Hand the leftover paise to the largest fractional parts. The index
  // tie-break keeps output deterministic for equal remainders.
  const order = exact
    .map((e, i) => ({ i, frac: e - Math.floor(e) }))
    .sort((a, b) => b.frac - a.frac || a.i - b.i);

  // `out` is a copy of `floors`, so index i always exists in both.
  const out = floors.slice();
  for (const { i } of order) {
    if (remainder <= 0) break;
    out[i] = (out[i] ?? 0) + 1;
    remainder--;
  }
  return out;
}

/** distribute as `allocate`, but typed as Money. */
export function allocateMoney(weights: readonly number[], total: Money): Money[] {
  return allocate(weights, total).map(asMoney);
}
